import { type ChangeEvent, type FocusEvent, type KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Input, InputStyles } from '@lmn/design-system';
import styled from 'styled-components';
import { debounce } from '@/utils/debounce.ts';
import { focusRing } from '@/app/components/search/styles/mixins.ts';

// The design's field is 48px, the label 4px from its top; the design system's is 52px.
const FIELD_HEIGHT = 48;

// Kallisto tokens over the design system's greys; its autofill mask paints the background, so that moves too.
const Field = styled(Input)`
  &,
  ${InputStyles.InnerWrapper}, ${InputStyles.ValueWrapper} {
    height: ${FIELD_HEIGHT}px;
  }

  ${InputStyles.LabelText} {
    top: 25px;
  }

  ${InputStyles.InnerWrapper} {
    background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundLayer01};

    &:focus-within {
      ${focusRing}
    }
  }

  &[aria-disabled='false'] ${InputStyles.LabelText} {
    color: ${({ theme }) => theme.kallisto.color.text.static.textSecondary};
  }

  ${InputStyles.Input} {
    margin-top: 22px;
    box-shadow: 0 0 0 1000px ${({ theme }) => theme.kallisto.color.background.static.backgroundLayer01} inset;

    &:not(:disabled) {
      color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
    }
  }
`;

type BudgetRangeInputProps = {
  label: string;
  displayValue: string;
  // Replaces displayValue on focus, so the user edits the amount rather than its formatting.
  editValue: string;
  disabled: boolean;
  onValueChange: (digits: string) => void;
};

const COMMIT_DELAY_MS = 500;

const digitsOf = (text: string) => text.replace(/\D/g, '');

export const BudgetRangeInput = ({
  label,
  displayValue,
  editValue,
  disabled,
  onValueChange,
}: BudgetRangeInputProps) => {
  const [draft, setDraft] = useState<string | null>(null);

  const onValueChangeRef = useRef(onValueChange);
  onValueChangeRef.current = onValueChange;

  // Compared against the bound, not the last sent value, so a refresh mid-edit cannot swallow the blur.
  const editValueRef = useRef(editValue);
  editValueRef.current = editValue;

  const report = (digits: string) => {
    if (digits === editValueRef.current) return;
    onValueChangeRef.current(digits);
  };
  const reportRef = useRef(report);
  reportRef.current = report;

  const commit = useMemo(() => debounce((digits: string) => reportRef.current(digits), COMMIT_DELAY_MS), []);
  useEffect(() => commit.cancel, [commit]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const digits = digitsOf(event.target.value);
    setDraft(digits);
    commit(digits);
  };

  const handleFocus = () => setDraft(editValue);

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    commit.cancel();
    setDraft(null);
    report(digitsOf(event.target.value));
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') event.currentTarget.blur();
  };

  return (
    <Field
      label={label}
      isFullWidth={true}
      disabled={disabled}
      value={draft ?? displayValue}
      onChange={handleChange}
      input={{
        inputMode: 'numeric',
        autoComplete: 'off',
        onFocus: handleFocus,
        onBlur: handleBlur,
        onKeyDown: handleKeyDown,
      }}
    />
  );
};
